/**
 * Category definitions, shared data types, and pure helper functions for Cibles.
 * No React hooks or stateful code — only static data, TypeScript types, and pure functions.
 */
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CategoryIcon from '@mui/icons-material/Category';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessIcon from '@mui/icons-material/Business';
import FaceIcon from '@mui/icons-material/Face';
import {
  TARGET_COLOR_PALETTE,
  type TargetCategory,
  type TargetColorToken,
  type TargetsManifest,
} from '@traaker/shared';
import { TARGET_COLOR_GREY, TARGET_COLOR_HEX, targetColorHex } from '../../utils/targetColors';

export { TARGET_COLOR_PALETTE };

// Re-export colour helpers under the short names used throughout this module family.
export const COLOR_HEX = TARGET_COLOR_HEX;
export const GREY = TARGET_COLOR_GREY;
export const colorHex = targetColorHex;

// ---------------------------------------------------------------------------
// Shared data types
// ---------------------------------------------------------------------------

export interface TargetRow {
  targetId: string;
  ownerId: string;
  category: TargetCategory;
  label: string;
  color?: TargetColorToken | null;
  parentTargetId?: string;
  /**
   * `notUnique` is the user's own override on a value — "this number is shared with salons
   * you don't know about" — and it round-trips, so reopening a fiche must show the toggle
   * the way it was left. Nothing else reads it here; the compiler is what acts on it.
   */
  fields: { name: string; value: any; notUnique?: boolean }[];
  /** Tag IDS. Names live in `manifest.tags` — resolve through `TagRegistry` below. */
  tags?: number[];
  /**
   * produit/collection targetIds (2026-10-06). Ids only — the home row carries no label for
   * them, so a picker shows the label only for a reference the user just chose.
   */
  relatedTargetIds?: string[];
  warnings?: { kind: string; field?: string; message: string }[];
  updatedAt: string;
}

/**
 * id → name, built once per manifest load (2026-09-23).
 *
 * The rows carry ids and the manifest carries the names, so nothing can render a chip without
 * both. An id with no entry is a RETIRED tag (ids are never reused) — it resolves to nothing
 * and the chip is dropped, exactly as the search Lambda drops it from a match.
 */
export type TagRegistry = Map<number, string>;

export function tagRegistryOf(manifest: TargetsManifest | null): TagRegistry {
  return new Map((manifest?.tags?.entries ?? []).map((e) => [e.id, e.name]));
}

export function tagNamesOf(row: TargetRow, registry: TagRegistry): string[] {
  return (row.tags ?? []).map((id) => registry.get(id)).filter((n): n is string => !!n);
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function catCount(manifest: TargetsManifest | null, cat: TargetCategory): number {
  if (!manifest?.countsByParentAndCategory) return 0;
  return Object.values(manifest.countsByParentAndCategory).reduce((sum, catMap) => {
    return sum + ((catMap as Record<string, number>)[cat] ?? 0);
  }, 0);
}

export function getFieldValues(row: TargetRow, fieldName: string, multi = false): string {
  const vals = row.fields.filter((f) => f.name === fieldName).map((f) => {
    if (typeof f.value === 'string') return f.value;
    return f.value?.label ?? '';
  }).filter(Boolean);
  return multi ? vals.join(', ') : (vals[0] ?? '');
}

// ---------------------------------------------------------------------------
// Category definitions
// ---------------------------------------------------------------------------

/**
 * `*Key` fields hold i18n keys, not copy — this table is module-level so it cannot call
 * `t()` itself; every consumer resolves them at render.
 */
export interface ColDef {
  field: string;
  labelKey: string;
  placeholderKey: string;
  width: number;
  optional?: boolean;
  /**
   * `enum`/`date` (2026-10-06) render a select / a date input. `enum` reads its options
   * from `FIELD_REGISTRY[category][field].values`.
   */
  type?: 'text' | 'url' | 'phone' | 'email' | 'zone' | 'enum' | 'date';
  multi?: boolean; // comma-separated multi-value (aliases, domains)
}

export interface CatConfig {
  id: TargetCategory;
  labelKey: string;
  Icon: React.ElementType;
  color: string;
  tint: string;
  descKey: string;
  addLabelKey: string;
  cols: ColDef[];
  hasParent: boolean; // needs a marque parent dropdown
  special?: 'textarea'; // Contenus — one URL per line
  /** When true, add/edit use a category-specific dialog instead of the generic AddRow. */
  customForm?: true;
  // Not i18n keys: the CSV templates are whole documents, already keyed by lang code
  // ('fr', 'en'), with 'fr' as the fallback.
  csvTemplates: Record<string, string>;
}

export const CATEGORIES: CatConfig[] = [
  {
    id: 'marque',
    labelKey: 'cibles.tab.marque',
    Icon: FingerprintIcon,
    color: '#7C3AED',
    tint: '#F5F3FF',
    descKey: 'cibles.desc.marque',
    addLabelKey: 'cibles.add.marque',
    hasParent: false,
    cols: [
      { field: 'name',   labelKey: 'cibles.col.marque.name',   placeholderKey: 'cibles.ph.marque.name',   width: 180 },
      { field: 'alias',  labelKey: 'cibles.col.marque.alias',  placeholderKey: 'cibles.ph.marque.alias',  width: 230, multi: true, optional: true },
      { field: 'domain', labelKey: 'cibles.col.marque.domain', placeholderKey: 'cibles.ph.marque.domain', width: 200, multi: true, optional: true },
    ],
    csvTemplates: {
      fr: [
        '# OBLIGATOIRE : au moins un champ parmi name, alias, domain.',
        '# Optionnels : alias (un alias par colonne), domain (un domaine par colonne).',
        '# Un identifiant trop commun (« Solution ») crée des faux positifs.',
        'name,alias,domain',
        'ACME,ACME Corp,acme.fr',
        'Acme Solutions,ACME,acme-solutions.fr',
      ].join('\n'),
      en: [
        '# REQUIRED: at least one of name, alias, domain.',
        '# Optional: alias (one per column), domain (one per column).',
        '# Too generic a name (e.g. "Agency") will cause false positives.',
        'name,alias,domain',
        'ACME,"ACME Corp",acme.com',
        'Acme Solutions,ACME,acme-solutions.com',
      ].join('\n'),
    },
  },
  {
    id: 'produit',
    labelKey: 'cibles.tab.produit',   // sub-tab 1 of Catalogue
    Icon: CategoryIcon,
    color: '#0D9488',
    tint: '#F0FDFA',
    descKey: 'cibles.desc.produit',
    addLabelKey: 'cibles.add.produit',
    hasParent: true,
    customForm: true as const,
    cols: [],
    csvTemplates: {
      fr: [
        '# Fichier unifié Produits + Catégories.',
        '# Colonnes OBLIGATOIRES : type (Product ou Category), url, marque_label',
        '# Obligatoire pour Product : id (GTIN, SKU ou ref interne)',
        '# Optionnelles : id (Category), name, buying_page (TRUE/FALSE, Product uniquement), tags (virgule)',
        '# Une ligne par URL produit — même id sur plusieurs lignes = plusieurs URLs (max 20)',
        'type,id,url,name,buying_page,tags,marque_label',
        '"Product","3760001570005","https://acme.fr/p/widget-pro","Widget Pro","TRUE","premium","ACME"',
        '"Product","3760001570005","https://acme.fr/m/widget-pro","Widget Pro","FALSE","","ACME"',
        '"Category","","https://acme.fr/c/accessoires/","Accessoires","","","ACME"',
        '"Category","CAT-001","https://acme.fr/c/electronique/","Électronique","","","ACME"',
      ].join('\n'),
      en: [
        '# Unified Products + Categories file.',
        '# Required columns: type (Product or Category), url, brand_label',
        '# Required for Product: id (GTIN, SKU or internal ref)',
        '# Optional: id (Category), name, buying_page (TRUE/FALSE, Product only), tags (comma)',
        '# One row per product URL — same id on multiple rows = multiple URLs (max 20)',
        'type,id,url,name,buying_page,tags,brand_label',
        '"Product","3760001570005","https://acme.com/p/widget-pro","Widget Pro","TRUE","premium","ACME"',
        '"Product","3760001570005","https://acme.com/m/widget-pro","Widget Pro","FALSE","","ACME"',
        '"Category","","https://acme.com/c/accessories/","Accessories","","","ACME"',
        '"Category","CAT-001","https://acme.com/c/electronics/","Electronics","","","ACME"',
      ].join('\n'),
    },
  },
  {
    id: 'collection',
    labelKey: 'cibles.tab.collection',   // sub-tab 2 of Catalogue
    Icon: CategoryIcon,
    color: '#0D9488',            // same teal — visually grouped with Produits
    tint: '#F0FDFA',
    descKey: 'cibles.desc.collection',
    addLabelKey: 'cibles.add.collection',
    hasParent: true,
    cols: [
      { field: 'url',  labelKey: 'cibles.col.collection.url',  placeholderKey: 'cibles.ph.collection.url',  width: 300, type: 'url' },
      { field: 'name', labelKey: 'cibles.col.collection.name', placeholderKey: 'cibles.ph.collection.name', width: 200, optional: true },
    ],
    csvTemplates: {
      // Same unified format as produit — use type=Category rows
      fr: [
        '# Même fichier que pour les Produits — utilisez type=Category pour les catégories.',
        '# Colonnes OBLIGATOIRES : type (Category), url, marque_label. Optionnelles : id, name, tags.',
        'type,id,url,name,tags,marque_label',
        '"Category","","https://acme.fr/c/accessoires/","Accessoires","","ACME"',
        '"Category","CAT-001","https://acme.fr/c/electronique/","Électronique","","ACME"',
      ].join('\n'),
      en: [
        '# Same file as for Products — use type=Category rows for catalogue sections.',
        '# Required columns: type (Category), url, brand_label. Optional: id, name, tags.',
        'type,id,url,name,tags,brand_label',
        '"Category","","https://acme.com/c/accessories/","Accessories","","ACME"',
        '"Category","CAT-001","https://acme.com/c/electronics/","Electronics","","ACME"',
      ].join('\n'),
    },
  },
  {
    id: 'contenu',
    labelKey: 'cibles.tab.contenu',
    Icon: ArticleIcon,
    color: '#2563EB',
    tint: '#EFF6FF',
    descKey: 'cibles.desc.contenu',
    addLabelKey: 'cibles.add.contenu',
    hasParent: true,
    cols: [
      { field: 'url', labelKey: 'cibles.col.contenu.url', placeholderKey: 'cibles.ph.contenu.url', width: 320, type: 'url' },
      { field: 'contentType', labelKey: 'cibles.col.contenu.contentType', placeholderKey: 'cibles.ph.contenu.contentType', width: 160, type: 'enum', optional: true },
      { field: 'publishedAt', labelKey: 'cibles.col.contenu.publishedAt', placeholderKey: 'cibles.ph.contenu.publishedAt', width: 150, type: 'date', optional: true },
    ],
    csvTemplates: {
      fr: [
        '# OBLIGATOIRE : url. Optionnel : marque_label (lie la source à une marque pour le surlignage).',
        '# Accepte aussi les préfixes de section : acme.fr/blog/ couvre tous les articles du blog.',
        "# content_type : laissez vide pour qu'il soit déduit du domaine de l'url.",
        "# published_at (AAAA-MM-JJ) : date de publication — c'est elle qui place le contenu sur la frise du tableau de bord « Contenus cités ».",
        '# related_products : références produit/catégorie séparées par des « ; » (id, SKU ou URL).',
        'url,marque_label,content_type,published_at,related_products',
        'https://acme.fr/blog/,ACME,,,',
        'https://acme.fr/expertises/,ACME,blog,2026-01-15,',
        'https://www.linkedin.com/pulse/acme-2026,ACME,,2026-02-01,SKU-123;https://acme.fr/p/serum',
      ].join('\n'),
      en: [
        '# REQUIRED: url. Optional: brand_label (links the source to a brand for highlighting).',
        '# Section prefixes are valid: acme.com/blog/ covers every article in that section.',
        "# content_type: leave empty to have it derived from the url's domain.",
        '# published_at (YYYY-MM-DD): publication date — it is what places the content on the « Cited contents » dashboard timeline.',
        '# related_products: product/collection references separated by ";" (id, SKU or URL).',
        'url,brand_label,content_type,published_at,related_products',
        'https://acme.com/blog/,ACME,,,',
        'https://acme.com/expertise/,ACME,blog,2026-01-15,',
        'https://www.linkedin.com/pulse/acme-2026,ACME,,2026-02-01,SKU-123;https://acme.com/p/serum',
      ].join('\n'),
    },
  },
  {
    id: 'etablissement',
    labelKey: 'cibles.tab.etablissement',
    Icon: BusinessIcon,
    color: '#059669',
    tint: '#ECFDF5',
    descKey: 'cibles.desc.etablissement',
    addLabelKey: 'cibles.add.etablissement',
    hasParent: true,
    cols: [
      { field: 'zone',    labelKey: 'cibles.col.etablissement.zone',    placeholderKey: 'cibles.ph.etablissement.zone',    width: 200, type: 'zone' },
      { field: 'name',    labelKey: 'cibles.col.etablissement.name',    placeholderKey: 'cibles.ph.etablissement.name',    width: 150 },
      { field: 'url',     labelKey: 'cibles.col.etablissement.url',     placeholderKey: 'cibles.ph.etablissement.url',     width: 160, optional: true },
      { field: 'phone',   labelKey: 'cibles.col.etablissement.phone',   placeholderKey: 'cibles.ph.etablissement.phone',   width: 130, optional: true, type: 'phone' },
      { field: 'placeId', labelKey: 'cibles.col.etablissement.placeId', placeholderKey: 'cibles.ph.etablissement.placeId', width: 150, optional: true },
    ],
    csvTemplates: {
      fr: [
        '# OBLIGATOIRE : zone (ville, adresse ou code postal + pays) et name.',
        '# Optionnels : url, phone (format international +33…), place_id (Google), marque_label.',
        '# zone : soyez précis — « Paris » seul est ambigu. Préférez « Paris, France » ou « 75001 Paris ».',
        '# marque_label : rattache l\'établissement à une marque pour les signaux de paire.',
        'zone,name,url,phone,place_id,marque_label',
        '"Paris, France","ACME Paris",acme.fr/agences/paris,+33123456789,ChIJN1t_tDeuEmsRUsoyG83frY4,ACME',
        '"Bruxelles, Belgique","ACME Bruxelles",acme.fr/agences/bruxelles,+3222334455,,ACME',
        '"Barcelona, España","ACME Barcelona",acme.es/tiendas/barcelona,+34912345678,,ACME',
      ].join('\n'),
      en: [
        '# REQUIRED: zone (city, address or postcode + country) and name.',
        '# Optional: url, phone (international format +1…), place_id (Google), brand_label.',
        '# zone: be specific — "Paris" alone is ambiguous. Prefer "Paris, France" or "75001 Paris".',
        '# brand_label: links the establishment to a brand for pair signals.',
        'zone,name,url,phone,place_id,brand_label',
        '"Paris, France","ACME Paris",acme.com/locations/paris,+33123456789,ChIJN1t_tDeuEmsRUsoyG83frY4,ACME',
        '"Brussels, Belgium","ACME Brussels",acme.com/locations/brussels,+3222334455,,ACME',
        '"Barcelona, Spain","ACME Barcelona",acme.es/stores/barcelona,+34912345678,,ACME',
      ].join('\n'),
    },
  },
  {
    id: 'personne',
    labelKey: 'cibles.tab.personne',
    Icon: FaceIcon,
    color: '#DB2777',
    tint: '#FDF2F8',
    descKey: 'cibles.desc.personne',
    addLabelKey: 'cibles.add.personne',
    hasParent: true,
    /**
     * Personne uses a custom dialog (PersonneAddForm) for both add and edit.
     * The generic AddRow / inline-edit path is not used for this category.
     */
    customForm: true as const,
    cols: [],
    csvTemplates: {
      fr: [
        '# OBLIGATOIRE : lastName.',
        '# Optionnels : title (Dr, Pr, …), firstName, email, phone (format international +33…), marque_label.',
        '# Les formes de citation (avec/sans titre, prénom + nom, nom seul) sont générées automatiquement.',
        'title,firstName,lastName,email,phone,marque_label',
        '"Dr","Antoine","Lévy",antoine@acme.fr,,ACME',
        '"","Marie","Martin",,+33612345678,ACME',
        '"Pr","Jean-Louis","Durand",jl.durand@acme.fr,+33145678901,ACME',
      ].join('\n'),
      en: [
        '# REQUIRED: lastName.',
        '# Optional: title (Dr, Prof, …), firstName, email, phone (international format +1…), brand_label.',
        '# Citation forms (with/without title, first + last, last only) are generated automatically.',
        'title,firstName,lastName,email,phone,brand_label',
        '"Dr","John","Smith",john@acme.com,,ACME',
        '"","Jane","Doe",,+16175551234,ACME',
        '"Prof","Alice","Johnson",a.johnson@acme.com,+12025551234,ACME',
      ].join('\n'),
    },
  },
];

export const CAT_BY_ID = Object.fromEntries(CATEGORIES.map((c) => [c.id, c])) as Record<TargetCategory, CatConfig>;
